/**
 * a small sample of fabric intended to demonstrate the look of a larger piece, paint, makeup, etc. applied to a surface or one skin to test or demonstrate the color.
 *
 * @module views/component/ProductSwatches
 * @memberof -Common
 */
import './ProductSwatches.scss';

import React, { useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import Button from '@ulta/core/components/Button/Button';
import { useToggle } from '@ulta/core/hooks/useToggle/useToggle';
import { hasItems } from '@ulta/core/utils/array/array';

import Swatch from '../Swatch/Swatch';



/**
 * Represents a ProductSwatches component
 *
 * @method
 * @param {ProductSwatchesProps} props - React properties passed from composition
 * @returns ProductSwatches
 */
export const ProductSwatches = function( props ){
  const [expanded, toggleExpanded] = useToggle( props.expanded );

  const clickHandler = () => {
    toggleExpanded( !expanded );
  };

  const swatchRef = useRef( null );
  const {
    variants,
    viewLessLabel,
    viewAllLabel,
    viewAllAction,
    collapsible,
    width = defaultProps.width,
    height = defaultProps.height
  } = props;

  const hasVariants = hasItems( variants );
  const [visibleSwatches, setVisibleSwatches] = useState( hasVariants ? variants : [] );

  // Todo need to add collapsible once dxl chnages done
  const showButton = variants.length > 45;
  useEffect( () => {
    if( showButton ){
      if( !expanded ){
        setVisibleSwatches( variants.slice( 0, 44 ) );
      }
      else {
        setVisibleSwatches( variants );
      }
    }
    else {
      setVisibleSwatches( variants );
    }
  }, [variants, expanded] );

  return (
    <div className='ProductSwatches'>
      <div ref={ swatchRef }
        className={ classNames( 'ProductSwatches--content' ) }
      >
        <ul>
          { visibleSwatches.map( ( item, index ) => {
            return (
              <li key={ `productSwatch:${index }` }>
                <Swatch
                  key={ index }
                  src={ item.swatchImage?.imageUrl }
                  alt={ item.swatchImage?.description }
                  outofStockLabel={ item.outofStockLabel }
                  selected={ item.selected }
                  disabled={ item.disabled }
                  variantUrl={ item.variantUrl }
                  unavailable={ item.unavailable }
                  showIcon={ true }
                  width={ width }
                  height={ height }
                  selectAction={ item.selectAction }
                  linkSelectAction={ item.linkSelectAction }
                  onSwatchClick={ () => {
                    props.selectAction( item );
                  } }
                  withHoverAndSelected={ true }
                />
              </li>
            );
          } )
          }
          { showButton && !expanded &&
          <li>
            <Button
              likeLink
              action={ viewAllAction }
              onClick={ clickHandler }
              label={ `+${variants.length - visibleSwatches.length}` }
              ariaLabel={ `${viewAllLabel} ${variants.length - visibleSwatches.length}` }
            />
          </li>
          }
          { showButton && expanded &&
          <li>
            <Button
              onClick={ clickHandler }
              action={ viewAllAction }
              likeLink
              label={ viewLessLabel }
              ariaLabel={ viewLessLabel }
            />
          </li>
          }
        </ul>
      </div>

    </div>
  );
};

/**
 * Property type definitions
 *
 * @typedef ProductSwatchesProps
 * @type {object}
 * @property {array} variants - This is the list of product received from backend for a specific query.
 * @property {boolean} expanded - Hold the value if filter is in expanded state at given time.
 * @property {boolean} collapsible - This sets the behavior of component that if it is collapsible or not.
 * @property {string} viewLessLabel - This is label for collapse button.
 * @property {string} viewAllLabel - This is aria-label for expand button.
 * @property {number} width - This is the width of swatch.
 * @property {number} height - This is the height of swatch.
 */

export const propTypes = {

  /** This is the list of product received from backend for a specific query.*/
  variants: PropTypes.arrayOf(
    PropTypes.shape( {
      productId: PropTypes.string,
      skuId: PropTypes.string,
      name: PropTypes.string,
      shadeDescription: PropTypes.string,
      swatchImage: PropTypes.shape( {
        imageUrl: PropTypes.string,
        description: PropTypes.string
      } ),
      smooshImage: PropTypes.shape( {
        imageUrl: PropTypes.string,
        description: PropTypes.string
      } ),
      selected: PropTypes.bool,
      disabled: PropTypes.bool,
      unavailable: PropTypes.bool,
      listPrice: PropTypes.string,
      salePrice: PropTypes.string,
      selectAction: PropTypes.oneOfType( [PropTypes.object, PropTypes.func] )
    } )
  ).isRequired,
  /** Hold the value if filter is in expanded state at given time.*/
  expanded: PropTypes.bool,
  /** This sets the behavior of component that if it is collapsible or not.*/
  collapsible: PropTypes.bool,
  /** This is label for collapse button.*/
  viewLessLabel: PropTypes.string,
  /** This is aria-label for expand button.*/
  viewAllLabel: PropTypes.string,
  /** This is the width of swatch.*/
  width: PropTypes.number,
  /** This is the height of swatch.*/
  height: PropTypes.number
};

/**
 * Default values for passed properties
 * @type object
 * @property {number} [ width = 40 ] - by default width is 40
 * @property {number} [ height = 40 ] - by default height is 40
 */
export const defaultProps = {
  width: 40,
  height: 40
};

ProductSwatches.propTypes = propTypes;
ProductSwatches.defaultProps = defaultProps;

export default ProductSwatches;
